import { ActorRefFrom, SnapshotFrom } from 'xstate';
import { z } from 'zod';
import { ERROR_MESSAGES } from '~/constants';
import { Provider, ProviderCategory } from '~/types';
import { providerRegistrationView } from './registration-view.machine';
import { providerRegistrationService } from './registration.service.machine';

// Welcome Form
export const WelcomeSchema = z
  .object({
    email: z.email('Invalid email address'),
    // This is a bit involved, but it allows us to have a required field that is only required if 'mfa' is true
    phone: z
      .string()
      .transform((val) => (val === '' ? undefined : val)) // Convert empty string to undefined
      .optional()
      .refine((val) => !val || /^\+[0-9][\d\s\-.()]+$/.test(val), 'Invalid phone number format')
      .transform((val) => (val ? val.replace(/[\s\-.()]/g, '') : undefined)) // Clean formatting
      .refine((val) => !val || (val.length >= 8 && val.length <= 16), 'Phone number length invalid')
      .refine((val) => !val || /^\+[0-9]\d{7,14}$/.test(val), 'Must be valid international format')
      .optional(),
    mfa: z.boolean(),
    tosAccepted: z.boolean().refine((value) => value, {
      message: ERROR_MESSAGES.REQUIRED,
    }),
    tosModalOpen: z.boolean(),
  })
  .refine(
    (data) => {
      if (data.mfa && (!data.phone || data.phone.length === 0)) {
        return false;
      }
      return true;
    },
    {
      message: ERROR_MESSAGES.REQUIRED,
      path: ['phone'],
    }
  );
export type WelcomeValues = z.infer<typeof WelcomeSchema>;
/**
 * How the AI parse that follows the upload ended.
 * - `pending`   still running (or not started)
 * - `complete`  the parse job reported a terminal success
 * - `failed`    the parse job reported a terminal failure
 * - `unknown`   we stopped watching before it settled (poll budget exhausted);
 *               the parse is still running server-side
 */
export type ParseOutcome = 'pending' | 'complete' | 'failed' | 'unknown';

export type CVViewContext = {
  dragging: boolean;
  uploading: boolean;
  uploaded: boolean;
  /** The upload finished but the AI parse has not settled yet. */
  parsing: boolean;
  parseOutcome: ParseOutcome;
  errorMsg: string | undefined;
  name: string | undefined;
  type: string | undefined;
  size: number | undefined;
  docType: 'cv' | 'packet';
  /**
   * A file the user picked but has not classified yet. [MCV-1593]
   *
   * The doc type question is asked *after* selection, so the file waits here
   * between the drop and the answer. It holds a `File`, never the source
   * `FileList`: `e.dataTransfer.files` enters protected mode once the drop
   * handler returns, so a deferred read of a stashed FileList can come back
   * empty, while a `File` reference stays valid indefinitely.
   *
   * Presence of this field is what renders the classify prompt, so it must be
   * cleared the instant the upload starts (otherwise it shadows the spinner --
   * see UploadPrompt's branch order and MCV-1590).
   */
  pendingFile?: File;
  documentReference?: {
    id: number;
    docName: string;
  };
};

// Type and Category Form
//
// `category` is always required. `type` is required only when the selected
// category actually has provider types — the Type dropdown is conditionally
// rendered on that same signal (see `selectedCategoryHasTypes` in
// TypeCategoryNPIForm). A category with no types must stay submittable, so the
// schema is parameterized on whether types are available rather than
// blanket-requiring `type`. Both fields keep a nullable value type (the form
// defaults them to null) but reject null at validation time so react-hook-form
// surfaces the inline "Required" prompt, matching first/last name. [MCV-1486]
const categoryOptionSchema = z.object({
  label: z.string(),
  value: z.number(),
  npiFlag: z.boolean().optional(),
});
const typeOptionSchema = z.object({
  label: z.string(),
  value: z.number(),
});

export const makeTypeAndCategorySchema = (categoryHasTypes: boolean) =>
  z.object({
    category: categoryOptionSchema
      .nullable()
      .refine((val): boolean => val !== null, { message: ERROR_MESSAGES.REQUIRED }),
    type: categoryHasTypes
      ? typeOptionSchema
          .nullable()
          .refine((val): boolean => val !== null, { message: ERROR_MESSAGES.REQUIRED })
      : typeOptionSchema.nullable(),
    firstName: z.string().min(2, ERROR_MESSAGES.REQUIRED),
    lastName: z.string().min(2, ERROR_MESSAGES.REQUIRED),
    npiNum: z
      .string()
      .regex(/^\d{10}$/, ERROR_MESSAGES.EXACT_CHARACTER_LIMIT(10))
      .or(z.literal(''))
      .optional(),
  });

// Base schema (type optional) — drives the inferred form value type and is the
// default resolver shape before a category with types is selected.
export const TypeAndCategorySchema = makeTypeAndCategorySchema(false);
export type TypeAndCategoryValues = z.infer<typeof TypeAndCategorySchema>;

export interface TypeAndCategoryValuesContext {
  category: CategorySelectOption | null;
  type: ReactSelectOption<number> | null;
  firstName: string;
  lastName: string;
  npiNum?: string;
}

export interface OptionType {
  label: string;
  value: number;
  npiFlag?: boolean;
}

export type SkipEvent = { type: 'ON_SKIP' };
export type SetPageEvent = { type: 'ON_SET_PAGE'; goTo: string };
export type PrevPageEvent = { type: 'ON_PREV_PAGE'; goTo: string };
export type DragEnterEvent = { type: 'ON_DRAG_ENTER' };
export type DragLeaveEvent = { type: 'ON_DRAG_LEAVE' };
export type SetLoadingEvent = { type: 'ON_SET_LOADING'; payload: { loading: boolean } };
export type CompleteRegistrationEvent = { type: 'ON_COMPLETE_REGISTRATION' };
export type OpenModalEvent = { type: 'ON_OPEN_MODAL' };
export type CloseModalEvent = { type: 'ON_CLOSE_MODAL' };
export type ResetErrorEvent = { type: 'ON_RESET_ERROR' };
export type RegistrationSubmitEvent = { type: 'ON_REGISTRATION_SUBMIT' };
export type UploadEvent = { type: 'ON_UPLOAD'; payload: { type: string; files: FileList } };
export type NextPageEvent = {
  type: 'ON_NEXT_PAGE';
  goTo: string;
  payload: WelcomeValues | TypeAndCategoryValues | undefined;
};

export type Events =
  | SkipEvent
  | NextPageEvent
  | PrevPageEvent
  | SetPageEvent
  | OpenModalEvent
  | CloseModalEvent
  | ResetErrorEvent
  | RegistrationSubmitEvent
  | SetLoadingEvent
  | CompleteRegistrationEvent
  | DragEnterEvent
  | DragLeaveEvent
  | UploadEvent;

export interface ViewContext {
  loading: boolean;
  welcomeForm: WelcomeValues;
  typeAndCategoryForm: TypeAndCategoryValuesContext;
  cvUpload: CVViewContext;
  provider?: Provider | undefined;
  clerkUser?: string;
  registrationService: ActorRefFrom<typeof providerRegistrationService>;
}

export interface Scope {
  context: ViewContext;
  event: Events;
}

/**
 * View machine context
 */
export interface RegistrationViewContext {
  registrationService?: ActorRefFrom<typeof providerRegistrationService>;
}

/**
 * React-select option shape for categories
 */
export interface CategorySelectOption {
  value: number;
  label: string;
  npiFlag?: boolean;
}

/**
 * React-select option shape for types
 */
export interface ReactSelectOption<T = number> {
  value: T;
  label: string;
}

/**
 * Select categories transformed to react-select options
 */
export const selectCategoryOptions = (
  snapshot: SnapshotFrom<typeof providerRegistrationService> | undefined
): CategorySelectOption[] => {
  if (!snapshot) return [];
  return snapshot.context.categories.map((c: ProviderCategory) => ({
    value: c.id,
    label: c.providerCategoryCode,
    npiFlag: c.npiFlag,
  }));
};

export interface ProviderType_ {
  id: number;
  name: string;
  code: string;
  categoryId: number;
}
/**
 * Select types transformed to react-select options
 */
export const selectTypeOptions = (
  snapshot: SnapshotFrom<typeof providerRegistrationView> | undefined
): ReactSelectOption<number>[] => {
  if (!snapshot) return [];
  return snapshot.context.typeOptions.map((t: ProviderType_) => ({
    value: t.id,
    label: t.code,
  }));
};

export interface CVUploadState {
  file?: File;
  uploading: boolean;
  error?: string;
  documentReference?: {
    id: number;
    docName: string;
  };
}
